import { STEditTemplateInlineWizardPage } from '@features/st-questionnaire-send/st-edit-template-inline-wizard-page'
import { ClientOrganizerScheduleOptions, FolderListRow } from '@st/sdk'
import { Banner, Button, CheckIcon, Spinner, TagChip, Wizard } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { formatTimeZone } from '@st/util/time'
import { clsx } from 'clsx'
import { useState } from 'react'
import { match } from '@st/pattern'
import { formatDays, formatSchedule, formatTimesOfDay } from './formatters'
import {
  BulkEnrollMessage,
  BulkEnrollState,
  ConfirmEnrollState,
  DEFAULT_SCHEDULE_OPTIONS,
  LAST_ALLOWED_END_DATE,
  SelectScheduleState
} from './st-enroll-in-reminders-module'

export function STEnrollInRemindersPage({
  state,
  folders,
  send,
  emailsManagedBy,
  onClose
}: {
  state: BulkEnrollState
  folders: FolderListRow[]
  send: (message: BulkEnrollMessage) => void
  emailsManagedBy: 'stanfordtax' | 'karbon'
  onClose: () => void
}) {
  if (state.status == 'idle') {
    return (
      <Wizard.Scaffold
        onClose={onClose}
        bottomNav={
          <Wizard.BottomNav>
            <Button variant="default" onClick={onClose}>
              Back
            </Button>
          </Wizard.BottomNav>
        }
      >
        <Wizard.Main
          variant="wide"
          title="Enroll clients in automated reminders"
          subtitle="No binders have been selected. Go back and select one or more binders to continue."
        >
          <div className="h-dvh" />
        </Wizard.Main>
      </Wizard.Scaffold>
    )
  }

  if (state.status == 'opening') {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner className="size-8 fill-blue-500 text-blue-100" />
      </div>
    )
  }

  switch (state.status) {
    case 'editTemplate':
      return (
        <STEditTemplateInlineWizardPage
          title="Enroll Clients in Automated Reminders"
          subtitle="If you enroll a client in automated reminders, StanfordTax will send them the following reminder every week on the schedule you select until they submit their questionnaire"
          messageType="client_organizer_submit_reminder"
          banner={
            emailsManagedBy == 'karbon' && (
              <Banner variant="info" className="sticky top-0 w-full rounded-none">
                {`StanfordTax reminders will take clients directly to their organizer. You can set up reminders in Karbon instead, which will take clients to their Karbon for Clients Portal.`}
              </Banner>
            )
          }
          onClose={onClose}
          onClickBack={onClose}
          onClickContinue={() => send({ type: 'continueToSelectSchedule' })}
        />
      )
    case 'selectSchedule':
      return <SelectScheduleWizardPage state={state} send={send} onClose={onClose} />
    case 'confirm':
      return <EnrollConfirmPage state={state} folders={folders} send={send} onClose={onClose} />
  }
}

function EnrollConfirmPage({
  state,
  // The workflow owns only selected IDs. The parent derives these folders from its realtime state
  // so enrollment feedback never depends on a synchronized snapshot.
  folders,
  send,
  onClose
}: {
  state: ConfirmEnrollState
  folders: FolderListRow[]
  send: (message: BulkEnrollMessage) => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" onClick={onClose}>
            Back
          </Button>
          {match(state.operationStatus)
            .with('idle', () => (
              <Button variant="primary" onClick={() => send({ type: 'confirmEnroll' })}>
                Enroll {state.folderIds.length} {inflect(state.folderIds.length, 'client')}
              </Button>
            ))
            .with('enrolling', () => (
              <Button
                variant="primary"
                leadingIcon={<Spinner className="size-4" />}
                disabled={true}
              >
                Enrolling...
              </Button>
            ))
            .with('enrolled', () => {
              return (
                <Button
                  variant="primary"
                  onClick={onClose}
                  leadingIcon={<CheckIcon className="h-4 w-4" />}
                >
                  Done
                </Button>
              )
            })
            .run()}
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main
        variant="wide"
        title={
          state.operationStatus == 'enrolled'
            ? 'Successfully enrolled clients in reminders!'
            : 'Enroll clients in automated reminders'
        }
        subtitle={
          state.operationStatus == 'enrolled' ? (
            'You successfully enrolled the clients in reminders'
          ) : (
            <div>
              You're about to enroll {state.folderIds.length}{' '}
              {inflect(state.folderIds.length, 'client')} in automated reminders.
              <br />
              StanfordTax will send these clients a reminder {formatSchedule(
                state.scheduleOptions
              )}{' '}
              until they submit their organizer.
            </div>
          )
        }
      >
        <STBulkEnrollTable folders={folders} />
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function STBulkEnrollTable({ folders }: { folders: FolderListRow[] }) {
  return (
    <table className="min-w-full table-fixed divide-y divide-gray-300">
      <thead>
        <tr className="h-9">
          <th scope="col" className="pl-8 text-left text-xs uppercase text-gray-600">
            Client
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Email
          </th>
          <th scope="col" className="pr-8 text-left text-xs uppercase text-gray-600">
            {/* Send Status */}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {folders.map((folder, index) => (
          <tr key={folder.id} className={clsx('h-12')}>
            <td className="whitespace-nowrap pl-8">
              <div className="flex flex-row items-center gap-2">
                <div className="text-base font-semibold text-gray-900">{folder.name}</div>
              </div>
            </td>
            <td className="whitespace-nowrap text-base text-gray-500">
              {folder.clientEmails.join(', ')}
            </td>
            <td className="whitespace-nowrap text-base text-gray-500">
              <div className="flex flex-row items-center">
                {folder.clientOrganizer.reminderSchedule && (
                  <TagChip color="green">Enrolled</TagChip>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function SelectScheduleWizardPage({
  state,
  send,
  onClose
}: {
  state: SelectScheduleState
  send: (message: BulkEnrollMessage) => void
  onClose: () => void
}) {
  const [schedule, setSchedule] = useState<ClientOrganizerScheduleOptions>(DEFAULT_SCHEDULE_OPTIONS)

  return (
    <Wizard.Scaffold
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" onClick={() => send({ type: 'backToEditTemplate' })}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => send({ type: 'continueToConfirm', scheduleOptions: schedule })}
          >
            Continue
          </Button>
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main
        variant="wide"
        title="Select Schedule"
        subtitle="Select the schedule for the reminders"
      >
        <div className="flex flex-col gap-6">
          <div className="text-xl">
            Reminders will be sent every{' '}
            <span className="font-semibold underline">{formatDays(schedule.daysOfWeek)}</span> at{' '}
            <span className="font-semibold underline">
              {formatTimesOfDay(schedule.timesOfDay)} {formatTimeZone(schedule.timeZone)}
            </span>
          </div>
          <div className="flex flex-row items-center gap-3 text-xl">
            Starting on{' '}
            <input
              type="date"
              value={schedule.firstDate}
              onChange={(e) => setSchedule({ ...schedule, firstDate: e.target.value })}
              min={state.today}
            />{' '}
            and ending on{' '}
            <input
              type="date"
              value={schedule.lastDate}
              onChange={(e) => setSchedule({ ...schedule, lastDate: e.target.value })}
              min={state.today}
              max={LAST_ALLOWED_END_DATE}
            />
          </div>
          {state.validationError && (
            <div className="text-center text-red-500">{state.validationError.message}</div>
          )}
        </div>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}
